import React, { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useEarliesTimeHasData from '@intelligence/hooks/useEarliesTimeHasData';
import useSenTowerTag from '@intelligence/hooks/useSenTowerTag';
import useDemographicsRegionsOptions from '@intelligence/hooks/useDemographicsRegionsOptions';
import useMetricsDateRange from '@intelligence/v3/_hooks/useMetricsDateRange';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { getMobileOverlapAnlysisCountryFilters } from '@intelligence/services/filter';
import { buildDateDisabledCallbackMap } from '@intelligence/v3/utils/date';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import {
  commonMobileDefaultFilterValue,
  handleOverlapFilterParamsToApi,
} from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { companyFilterParams } from './utils';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { SENSOR_TOWER_TAG_KEYS } from '@intelligence/components/Filter/FilterBase/_config';
import { GENRE_SOURCES_NEW } from '@intelligence/components/GenreSelect/constants';
import OverlapAnalysisContext from './context';

import type { TopchartFilterCfgType } from '@intelligence/types/topchart';

import '@intelligence/assets/scss/OverlapAnalysis/index.scss';
import { DATASOURCE_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics';

interface IOverlapAnalysisContentProps {
  pageLogConfigKey: string;
  downloadLogConfigKey: string;
  /** 是否禁用筛选器分组（平铺模式），默认 false（启用分组） */
  disableFilterGrouping?: boolean;
}

const OverlapAnalysisContent: React.FC<IOverlapAnalysisContentProps> = (props) => {
  const { pageLogConfigKey, disableFilterGrouping = false, ...restProps } = props;
  // 新增：获取品类定义数据
  const { loading: definitionsLoading, genreMap } = useQueryDefinition();

  // 新增：获取SensorTower Tag配置
  const { tagLoading, tagConfig } = useSenTowerTag({});

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.overlapAnalysis?.type,
  });

  const { marketLoading, regionOptions, allMarkets } = useDemographicsRegionsOptions({
    appIds: [],
    getCountriesFilterFun: () => {
      return getMobileOverlapAnlysisCountryFilters({ game_ids: ['all'] });
    },
  });

  const {
    // 是否加载中
    loading: metricsGranularityDateRangesLoading,
    // 指标的日期粒度与有数据日期范围的映射
    granularityDateRangeMap: metricsGranularityDateRangeMap,
  } = useMetricsDateRange(!marketLoading, {
    game_ids: [],
    app_ids: [],
    granularities: ['monthly'],
    markets: getAllCountryCodesByRegions(regionOptions),
    metrics: [
      'sensortower_mau',
      DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE,
      DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY,
    ],
    group_by: ['market'],
  });

  const rangePickerDisabledMap = useMemo<IDateRangeDisabledCallbackDateTypeMap>(() => {
    if (metricsGranularityDateRangesLoading) {
      return {};
    }

    return buildDateDisabledCallbackMap(metricsGranularityDateRangeMap);
  }, [metricsGranularityDateRangesLoading, metricsGranularityDateRangeMap]);

  const filterLoading = definitionsLoading || tagLoading || marketLoading || metricsGranularityDateRangesLoading;

  // 热门标签选项
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');

  // 当前选中的 hotTag option，用于下载文件名
  const [selectedHotTagOption, setSelectedHotTagOption] = useState<any>(null);
  // 匹配 filterTagName 后解析出的筛选参数，传给 Context 作为初始值
  const [hotTagFilterParams, setHotTagFilterParams] = useState<any>(null);
  const [hotTagDefaultOption, setHotTagDefaultOption] = useState<any>(null);
  const hotTagResolvedInit = !filterTagName;
  const [hotTagResolved, setHotTagResolved] = useState(hotTagResolvedInit);

  const { locale } = useIntl();

  useEffect(() => {
    if (!filterTagName || hotTagsLoading) return;

    if (!hotTagsOptions.length) {
      setHotTagResolved(true);
      return;
    }

    const matchedOption = hotTagsOptions.find((opt) => opt?.name === filterTagName);
    if (!matchedOption?.value) {
      setHotTagResolved(true);
      return;
    }
    setSelectedHotTagOption(matchedOption);
    setHotTagDefaultOption(matchedOption);
    parseUrlFilterParams(matchedOption.value, {})
      .then((parsedValue) => {
        if (parsedValue && Object.keys(parsedValue).length > 0) {
          setHotTagFilterParams(parsedValue);
        }
        setHotTagResolved(true);
      })
      .catch((err) => {
        console.error('[OverlapAnalysis] hotTag parseUrlFilterParams failed:', err);
        setHotTagResolved(true);
      });
  }, [filterTagName, hotTagsLoading, hotTagsOptions]);

  // 下载文件名前缀
  const downloadFileNamePrefix = selectedHotTagOption
    ? (locale === 'zh-CN'
      ? (selectedHotTagOption.name_cn || selectedHotTagOption.name || '')
      : (selectedHotTagOption.name_en || selectedHotTagOption.name || ''))
    : '';

  const defaultFilterValue = {
    ...commonMobileDefaultFilterValue,
    [filterKeyMap.MOBILE_OVERLAP]: {
      value: { opt: '', value: '' },
    },
    // 新增：SensorTower Tag筛选器默认值
    [filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_SETTING]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_TAGS]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_THEME]: {
      value: [],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION]: {
      value: [],
    },
  };

  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.RANGE_TIME,
          filterKeyMap.COMPOSED_COUNTRY_SELECT,
          filterKeyMap.GENRETOR,
          filterKeyMap.HOT_TAGS_SOURCE,
          // filterKeyMap.PLATFORM, // 隐藏Platform筛选器
          filterKeyMap.ALL_COMPANYS,
          filterKeyMap.RELEASE_TIME,
        ],
      },
      {
        key: 'game_performance',
        titleKey: 'dbi_filter_group_game_performance',
        filterKeys: [filterKeyMap.MOBILE_OVERLAP],
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: SENSOR_TOWER_TAG_KEYS,
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );

  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: groupedFilter.filterKeyList,
    filterGroups: disableFilterGrouping ? undefined : groupedFilter.filterGroups,
    companyFilterParams,
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
      },
    },
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    enableSticky: true,
    handleFilterToApiParams: handleOverlapFilterParamsToApi,
    specialParams: {
      myFilterStyle: { width: 140 },
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      entityType: 'mobile',
      regionOptions, // 国家筛选器options,
      defalutMarket: ['us'], // 没取到交集用的默认值
      disableGlobal: true,
      disableNoChina: true,
      rangeTimeType: FilterTimeType.rangeMonth,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false, // 启用SensorTower数据源
      genreSources: GENRE_SOURCES_NEW, // 新增：支持三个数据源的品类筛选器
      genreOptionsLoading: definitionsLoading, // 新增：品类数据加载状态
      genreOptions: genreMap, // 新增：品类数据
      sensorTowerTagConfig: tagConfig, // 新增：SensorTower Tag配置
      isSimpleCountry: true,
      isStdGenreValuesHandler: true,
      // 热门标签筛选器相关
      hotTagsOptions: hotTagsOptions || [],
      filterTagName,
      hotTagDefaultOption,
    },
  };

  usePageLog(pageLogConfigKey);

  return (
    <OverlapAnalysisContext
      {...restProps}
      pageLogInfo={dbiPageLogMes[pageLogConfigKey]}
      filterCfg={filterCfg}
      filterLoading={filterLoading || !hotTagResolved}
      allMarkets={allMarkets}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      rangePickerDisabledMap={rangePickerDisabledMap}
      hotTagFilterParams={hotTagFilterParams}
      downloadFileNamePrefix={downloadFileNamePrefix}
      onHotTagConfirm={setSelectedHotTagOption}
    />
  );
};

export default OverlapAnalysisContent;
